import React, { useMemo, useState } from "react";
import { Button, Input, Modal } from "antd";
import { PlayCircleFilled, SearchOutlined, VideoCameraOutlined } from "@ant-design/icons";
import { groupByCategory, searchItems } from "./helpSearch";
import { LIVE_TRAINING_URL } from "./helpCenterConfig";
import { videoSearchFields } from "./helpVideos";

export const VideoCard = ({ video, onPlay }) => {
   const thumbnail = video.thumbnail || video.source.thumbnail;

   return (
      <button type="button" className="help-video" onClick={() => onPlay(video)}>
         <span className="help-video__thumb">
            {thumbnail && <img src={thumbnail} alt="" loading="lazy" />}
            <PlayCircleFilled className="help-video__play" />
            {video.source.interactive && (
               <span className="help-video__badge">Interactive demo</span>
            )}
            {video.duration && <span className="help-video__duration">{video.duration}</span>}
         </span>
         <span className="help-video__text">
            <span className="help-video__title">{video.title}</span>
            {video.description && (
               <span className="help-video__description">{video.description}</span>
            )}
         </span>
      </button>
   );
};

/** Plays `video` in a modal; nothing shows while it is null. */
export const VideoPlayer = ({ video, onClose }) => (
   <Modal
      open={Boolean(video)}
      onCancel={onClose}
      title={video?.title}
      footer={null}
      width={960}
      centered
      destroyOnClose
      rootClassName="help-video-modal"
   >
      {video && (
         <>
            <div className="help-video-modal__frame">
               {video.source.type === "file" ? (
                  <video src={video.source.src} controls autoPlay />
               ) : (
                  <iframe
                     src={video.source.src}
                     title={video.title}
                     allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                     allowFullScreen
                  />
               )}
            </div>
            {video.description && (
               <p className="help-video-modal__description">{video.description}</p>
            )}
         </>
      )}
   </Modal>
);

/** Drawer view listing the help videos; plays the chosen one in a modal. */
const VideoLibrary = ({ videos }) => {
   const [query, setQuery] = useState("");
   const [playing, setPlaying] = useState(null);
   const groups = useMemo(() => groupByCategory(searchItems(videos, query, videoSearchFields)), [
      videos,
      query
   ]);

   if (videos.length === 0) {
      return (
         <div className="help-center__empty">
            <span className="help-center__empty-icon">
               <VideoCameraOutlined />
            </span>
            <h4>Videos are on the way</h4>
            <p>
               We are recording short walkthroughs of the main KAPsurvey features. Until they are
               ready, join one of our live trainings.
            </p>
            <Button href={LIVE_TRAINING_URL} target="_blank" rel="noopener noreferrer">
               See live trainings
            </Button>
         </div>
      );
   }

   return (
      <>
         <Input
            allowClear
            className="help-center__search"
            placeholder="Search videos"
            prefix={<SearchOutlined />}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
         />

         {groups.length === 0 ? (
            <p className="help-center__no-results">No videos match “{query.trim()}”.</p>
         ) : (
            groups.map((group) => (
               <section key={group.category || "uncategorized"} className="help-center__section">
                  {group.category && (
                     <h5 className="help-center__section-title">{group.category}</h5>
                  )}
                  <div className="help-center__videos">
                     {group.items.map((video) => (
                        <VideoCard key={video.id} video={video} onPlay={setPlaying} />
                     ))}
                  </div>
               </section>
            ))
         )}

         <VideoPlayer video={playing} onClose={() => setPlaying(null)} />
      </>
   );
};

export default VideoLibrary;
